import React from 'react';
import { Badge } from '../common/Badge';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import { Raffle } from '../../types';
import { CountdownTimer } from '../common/CountdownTimer';

interface DrawPeriodsProps {
  raffle: Raffle;
  variant?: 'full' | 'compact';
}

export const DrawPeriods: React.FC<DrawPeriodsProps> = ({ raffle, variant = 'full' }) => {
  const { pendingDraw, nextDraw } = raffle;

  if (!pendingDraw && nextDraw) {
    return (
      <div className={variant === 'full' ? 'space-y-4' : ''}>
        <div className={variant === 'full' ? 'bg-indigo-50 dark:bg-indigo-950 p-4 rounded-xl border border-indigo-100 dark:border-indigo-900' : 'bg-indigo-50 dark:bg-indigo-950 p-2 rounded-lg border border-indigo-100 dark:border-indigo-900'}>
          <div className="flex items-center justify-between mb-2">
            <p className={`${variant === 'full' ? 'text-sm' : 'text-xs'} text-indigo-600 dark:text-indigo-400 font-medium flex items-center gap-1`}>
              <Calendar className={variant === 'full' ? 'h-4 w-4' : 'h-3 w-3'} />
              Next Draw
            </p>
            <Badge variant="info">{nextDraw.status}</Badge>
          </div>
          {variant === 'full' ? (
            <>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{formatDateTime(nextDraw.scheduledTime)}</p>
              <CountdownTimer target={nextDraw.scheduledTime} variant="card" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                  <p className="text-xs text-indigo-500 dark:text-indigo-400">Tickets Sold</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{nextDraw.ticketsSold}</p>
                </div>
                <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                  <p className="text-xs text-indigo-500 dark:text-indigo-400">Current Draw Pot</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(nextDraw.sales, raffle.currency)}</p>
                </div>
              </div>
            </>
          ) : (
            <CountdownTimer target={nextDraw.scheduledTime} variant="card" subtext={formatDateTime(nextDraw.scheduledTime)} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={variant === 'full' ? 'space-y-4' : 'space-y-2'}>
      {pendingDraw && (
        <div className={variant === 'full' ? 'bg-amber-50 dark:bg-amber-950 p-4 rounded-xl border-2 border-amber-200 dark:border-amber-800' : 'bg-amber-50 dark:bg-amber-950 p-2 rounded-lg border-2 border-amber-200 dark:border-amber-800'}>
          <div className="flex items-center justify-between mb-2">
            <p className={`${variant === 'full' ? 'text-sm' : 'text-xs'} text-amber-700 dark:text-amber-400 font-medium flex items-center gap-1`}>
              <AlertCircle className={variant === 'full' ? 'h-4 w-4' : 'h-3 w-3'} />
              Pending Draw
            </p>
            <Badge variant="warning">DELAYED</Badge>
          </div>
          <p className={`${variant === 'full' ? 'text-sm' : 'text-xs'} text-amber-600 dark:text-amber-400`}>
            Processing — results coming soon
          </p>
          {variant === 'full' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">Tickets Locked</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{pendingDraw.ticketsSold}</p>
              </div>
              <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">Current Draw Pot</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(pendingDraw.sales, raffle.currency)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {nextDraw && (
        <div className={variant === 'full' ? 'bg-emerald-50 dark:bg-emerald-950 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900' : 'bg-emerald-50 dark:bg-emerald-950 p-2 rounded-lg border border-emerald-100 dark:border-emerald-900'}>
          <div className="flex items-center justify-between mb-2">
            <p className={`${variant === 'full' ? 'text-sm' : 'text-xs'} text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-1`}>
              <TrendingUp className={variant === 'full' ? 'h-4 w-4' : 'h-3 w-3'} />
              Next Draw
            </p>
            <Badge variant="success">{nextDraw.status}</Badge>
          </div>
          {variant === 'full' ? (
            <>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{formatDateTime(nextDraw.scheduledTime)}</p>
              <CountdownTimer target={nextDraw.scheduledTime} variant="card" />
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">Tickets Sold</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{nextDraw.ticketsSold}</p>
                </div>
                <div className="bg-white/60 dark:bg-white/5 rounded-lg p-2">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">Current Draw Pot</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(nextDraw.sales, raffle.currency)}</p>
                </div>
              </div>
            </>
          ) : (
            <CountdownTimer target={nextDraw.scheduledTime} variant="card" subtext={formatDateTime(nextDraw.scheduledTime)} />
          )}
        </div>
      )}
    </div>
  );
};
